import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Plus, Trash2, MapPin } from "lucide-react";
import { toast } from "sonner";

export default function OverridesEditor({ onChanged }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ start_date: "", end_date: "", location: "", note: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/presence/overrides");
      setItems(data);
    } catch (e) { /* non-fatal */ }
  };

  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/presence/overrides", form);
      toast.success("Override added");
      setForm({ start_date: "", end_date: "", location: "", note: "" });
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not add override");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/presence/overrides/${id}`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="rounded-card border border-hairline bg-surface" data-testid="overrides-editor">
      <div className="px-5 py-4 border-b border-sand-200">
        <p className="label-tag">Manual overrides</p>
        <p className="text-xs text-sand-500 mt-1">
          Force a date range to a location when the engine is wrong (e.g. road trips, missing flights). Airport code, state, or country.
        </p>
      </div>

      <form onSubmit={add} className="px-5 py-4 border-b border-sand-100 grid grid-cols-2 md:grid-cols-[150px_150px_1fr_1fr_auto] gap-3 items-end">
        <div>
          <label className="label-tag text-[9px] block mb-1.5">From</label>
          <input type="date" required value={form.start_date}
            onChange={(e) => setForm({ ...form, start_date: e.target.value })}
            className="swiss-input text-sm py-2" data-testid="override-start-input" />
        </div>
        <div>
          <label className="label-tag text-[9px] block mb-1.5">To</label>
          <input type="date" required value={form.end_date}
            onChange={(e) => setForm({ ...form, end_date: e.target.value })}
            className="swiss-input text-sm py-2" data-testid="override-end-input" />
        </div>
        <div>
          <label className="label-tag text-[9px] block mb-1.5">Location</label>
          <input type="text" required placeholder="SJU, CO, Mexico…" value={form.location}
            onChange={(e) => setForm({ ...form, location: e.target.value })}
            className="swiss-input text-sm py-2" data-testid="override-location-input" />
        </div>
        <div>
          <label className="label-tag text-[9px] block mb-1.5">Note</label>
          <input type="text" placeholder="optional" value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            className="swiss-input text-sm py-2" data-testid="override-note-input" />
        </div>
        <button type="submit" disabled={busy} className="btn-swiss btn-primary text-xs disabled:opacity-50 h-[38px]" data-testid="override-add-btn">
          <Plus size={14} className="mr-1.5" /> Add
        </button>
      </form>

      {items.length === 0 ? (
        <p className="px-5 py-6 text-sm text-sand-500">No overrides.</p>
      ) : (
        <ul className="divide-y divide-sand-50">
          {items.map((ov) => (
            <li key={ov.id} className="px-5 py-2.5 flex items-center gap-3" data-testid={`override-row-${ov.id}`}>
              <MapPin size={13} className="text-sand-400 shrink-0" />
              <span className="font-mono text-xs text-sand-500 w-48 shrink-0">{ov.start_date} → {ov.end_date}</span>
              <span className="text-sm font-medium text-sand-900">{ov.name}</span>
              <span className="font-mono text-[10px] text-sand-400">{ov.code}</span>
              {ov.note && <span className="text-xs text-sand-500 truncate flex-1">· {ov.note}</span>}
              <button
                onClick={() => remove(ov.id)}
                className="ml-auto text-sand-300 hover:text-error transition-colors"
                title="Delete"
                data-testid={`delete-override-${ov.id}`}
              >
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
